import { WritableDraft } from "immer";
import values from "lodash/values";
import {
  areFilterValuesEmpty,
  areValuesEqual,
  getEmptyValue,
  getFiltersSubset
} from "PFCore/helpers/filters";
import { FiltersV2, FilterV2, FilterV2WithValueOnly } from "PFTypes/filters_v2";

import { FiltersV2Data, ResetFilterValuesOptions } from "../filters_v2_store.types";

type ResetValuesOptions = Pick<ResetFilterValuesOptions, "ignoreFilterDefaultValues"> & {
  defaults?: FiltersV2<FilterV2WithValueOnly>;
};

/**
 * @param filters state to update
 * @param defaults default values to set
 * @return list of updated filters
 */
export const resetValues = (
  filters: WritableDraft<FiltersV2>,
  { defaults, ignoreFilterDefaultValues }: ResetValuesOptions = {}
): FilterV2[] => {
  const getNewValue = (filter: FilterV2) =>
    defaults?.[filter.name]?.value ??
    (!ignoreFilterDefaultValues ? filter.defaultValue : undefined) ??
    (filter.required ? filter.value : undefined) ??
    getEmptyValue(filter);

  const filtersToUpdate = values(filters).filter(
    (filter) => !areValuesEqual(getNewValue(filter), filter.value)
  );

  filtersToUpdate.forEach((filter) => {
    filter.value = getNewValue(filter);
    filter.error = null;
  });

  return filtersToUpdate;
};

export const resetFilterValues = (
  draftState: WritableDraft<FiltersV2Data>,
  { defaults, subset, ignoreFilterDefaultValues, removeChildrenValues = true }: ResetFilterValuesOptions = {}
) => {
  const updatedChildren = resetValues(getFiltersSubset(draftState.childrenFilters, subset), {
    defaults: defaults?.childrenFilters,
    ignoreFilterDefaultValues: ignoreFilterDefaultValues || removeChildrenValues
  });
  draftState.areChildrenOn = !areFilterValuesEmpty(draftState.childrenFilters);

  return [
    ...resetValues(getFiltersSubset(draftState.mainFilters, subset), {
      defaults: defaults?.mainFilters,
      ignoreFilterDefaultValues
    }),
    ...updatedChildren
  ];
};
